import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button, Checkbox } from "antd";
import { Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import {
  fetchAnalysisJobs,
  selectActiveJobForActiveDocument,
  selectAnalysisJobsForActiveDocument,
  selectIsAnalysisRunningForDocument,
  selectJobsForDocument,
  selectJobsForMatter,
  triggerAnalysisJob,
} from "../../slice/AISlice";
import usePollAnalysisJob from "../../hooks/usePollAnalysisJob";
import AnalysisProgressView from "./AnalysisProgressView";
import AnalysisReport from "./AnalysisReport";
import {
  AIPaneScrollBody,
  AnalysisEmptyDescription,
  AnalysisEmptyIconRing,
  AnalysisEmptyTitle,
  AnalysisEmptyWrapper,
  AnalysisErrorBanner,
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
} from "./AIPaneStyled";

/**
 * The Brief tab (internal key "analysis").
 *
 * MATTER-FIRST since 2026-08-18: what this tab renders is a function
 * of the MATTER, never of the document open in the viewer. Matthew:
 * "that side window just needs to stay static. It should be unaffected
 * by anything I do in the other windows." The active document enters
 * exactly once — as the POST target when the user starts a NEW run —
 * and is read at click time, never reactively.
 *
 * Hydrates the matter-wide jobs list on mount (works at the matter
 * root with no document open), kicks off polling via
 * `usePollAnalysisJob` whenever an active job is in flight, and
 * renders one of four states:
 *
 *   1. No matterId AND no      → "No document selected" empty state
 *      documentId                (legacy doc-scoped surfaces only).
 *   2. Hydration loading       → skeleton (idle/loading on first paint).
 *   3. Active job (running OR  → AnalysisProgressView with status pill,
 *      terminal but cached)      progress bar, condition checklist,
 *                                error banner — or AnalysisReport for
 *                                completed jobs.
 *   4. No jobs ever            → "Run AI Analysis" empty-state CTA
 *                                (Run disabled until a document is
 *                                open to POST against).
 *
 * The polling hook lives here (not inside AnalysisProgressView) so a
 * job continues to poll while the user is on a different AI tab —
 * AIPane keeps every tab mounted (preserves Chat history), so
 * AnalysisTab is mounted whenever the pane is open.
 */
const AnalysisTab = ({ documentId, matterId, isActive }) => {
  const dispatch = useDispatch();
  // MATTER-FIRST (Matthew, 2026-08-18): the Brief is a matter-wide
  // artifact and the right sidebar must stay static — nothing done in
  // the other windows (switching documents, opening evidence, closing
  // tabs) may change what this pane shows. The selector resolves the
  // matter cache first whenever `matterId` is present; the active
  // document only matters as the trigger target for NEW runs. See
  // `selectAnalysisJobsForActiveDocument` for the exact order (one
  // freshness exception lets a polled terminal per-doc copy of the
  // SAME job beat a stale "running" matter entry).
  const jobsState = useSelector((state) =>
    selectAnalysisJobsForActiveDocument(state, documentId, matterId)
  );
  // Raw per-document slot (NOT the merged matter-first view).
  // Bugbot-flagged: the hydration effect below MUST gate on the
  // per-doc slot's status — not on the merged `jobsState` — so the
  // per-document fetch fires even when the matter cache is already
  // hydrated. Downstream selectors (`selectIsAnalysisRunningForDocument`,
  // `selectRunningAnalysisDocIds`) read directly from `jobsByDocument`,
  // so without this backfill the matter-table Analyze button + AIStatusBadge
  // would show stale state for the jumped-to document. Render still uses
  // the merged `jobsState` so the skeleton-flash fix is preserved.
  const docJobsState = useSelector((state) =>
    selectJobsForDocument(state, documentId)
  );
  // Raw matter slot — drives matter-scoped hydration (which must fire
  // even at the matter root, where documentId is null).
  const matterJobsState = useSelector((state) =>
    selectJobsForMatter(state, matterId)
  );
  const isRunning = useSelector((state) =>
    selectIsAnalysisRunningForDocument(state, documentId)
  );
  // Active job = running job (if any), else newest cached job. Drives
  // the progress view and feeds the polling hook. Selector returns
  // null if no jobs exist for this matter/doc yet.
  const activeJob = useSelector((state) =>
    selectActiveJobForActiveDocument(state, documentId, matterId)
  );

  // Kick off / tear down polling based on activeJob.status. Hook is a
  // no-op when documentId/job is missing or status is terminal, so we
  // can safely call it unconditionally — keeps the hook order stable
  // across renders. Poll under the job's OWN document (matter-first:
  // the rendered job usually belongs to a different document than the
  // one open in the viewer — and at the matter root there is no open
  // document at all). Falls back to the viewer document for synthetic
  // jobs written before the `document` field existed in the cache.
  usePollAnalysisJob({
    documentId: activeJob?.document ?? documentId,
    job: activeJob,
  });

  const [claimedOnly, setClaimedOnly] = useState(true);
  // Track trigger-thunk pending separately from the shared
  // jobsByDocument.status (which is also "loading" during the
  // mount-time hydration fetch). Without this split the Run button
  // would show a spinner on first tab activation before the user has
  // ever clicked it, because the hydration fetch and the trigger
  // dispatch share the same Redux status field.
  const [isTriggering, setIsTriggering] = useState(false);
  // Set when a trigger 429s with `queueable: true` (the user is at
  // their concurrent-Brief cap, BE PR #1898). Holds the running
  // Brief's progress snapshot so the pane can offer "Queue this
  // Brief" instead of the dead-end red error. Cleared on a successful
  // (re-)trigger and on subject change.
  const [queueOffer, setQueueOffer] = useState(null);

  // Reset the claimed-condition filter when the pane's SUBJECT changes
  // — the matter when we have one, the document only on legacy
  // doc-scoped surfaces. Keying this on the document (the pre-2026-08-18
  // behavior) made the sidebar react to viewer navigation: every doc
  // switch silently wiped the user's checkbox choice even though the
  // matter-wide Brief they were configuring hadn't changed.
  const resetKey = matterId ?? documentId;
  useEffect(() => {
    setClaimedOnly(true);
    setIsTriggering(false);
    setQueueOffer(null);
  }, [resetKey]);

  // Matter-scoped hydration: we fetch the matter-wide jobs list (one
  // analysis spans the whole claim file in the prototype contract).
  // The fetch fires when EITHER cache slot is idle:
  //   - matter slot idle → first open of the pane on this matter.
  //     Works with documentId == null too (matter root) — the thunk
  //     accepts matter-only args as of 2026-08-18.
  //   - per-doc slot idle → background backfill on doc switch. Gate on
  //     the RAW per-doc slot, not the merged `jobsState` (Bugbot): the
  //     merged selector reports `ready` whenever the matter cache has
  //     data, which would leave `jobsByDocument[documentId]` empty and
  //     starve `selectIsAnalysisRunningForDocument` /
  //     `selectRunningAnalysisDocIds` (matter-table Analyze button +
  //     AIStatusBadge). This backfill never touches what the pane
  //     RENDERS — the pending reducer refuses to downgrade a ready
  //     matter cache, so the sidebar stays put.
  // `failed` fetches once per documentId — same-doc tab toggles are
  // deduped by ref so the user clicks Retry; doc switches (A → B → A)
  // DO re-fetch since the ref no longer holds A. `loading` / `ready`
  // are no-ops.
  const lastFetchedDocIdRef = useRef(null);
  useEffect(() => {
    if (!isActive || (!documentId && !matterId)) return;
    const matterIdle = matterId != null && matterJobsState.status === "idle";
    const docIdle = documentId != null && docJobsState.status === "idle";
    if (matterIdle || docIdle) {
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchAnalysisJobs({ documentId, matterId }));
      return;
    }
    if (
      documentId != null &&
      docJobsState.status === "failed" &&
      lastFetchedDocIdRef.current !== documentId
    ) {
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchAnalysisJobs({ documentId, matterId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, documentId, matterId, isActive]);

  const handleRetry = useCallback(() => {
    if (!documentId && !matterId) return;
    lastFetchedDocIdRef.current = documentId;
    dispatch(fetchAnalysisJobs({ documentId, matterId }));
  }, [dispatch, documentId, matterId]);

  const handleRun = async () => {
    if (!documentId || isRunning || isTriggering) return;
    setIsTriggering(true);
    try {
      const result = await dispatch(
        triggerAnalysisJob({ documentId, claimedOnly, matterId })
      );
      if (
        triggerAnalysisJob.rejected.match(result) &&
        result.payload?.queueable &&
        result.payload?.running_brief
      ) {
        // At the concurrent-Brief cap: offer to queue instead of
        // rendering the raw error banner.
        setQueueOffer({ runningBrief: result.payload.running_brief });
      } else {
        setQueueOffer(null);
      }
    } finally {
      setIsTriggering(false);
    }
  };

  // Re-trigger with `queue: true` — creates the job as status="queued";
  // the synthetic job from the fulfilled reducer then takes over the
  // pane (progress view in its queued state) and polling picks up the
  // promotion to running. Preserves the claimed_only choice and the
  // matterId exactly like the Run button.
  const handleQueue = async () => {
    if (!documentId || isTriggering) return;
    setIsTriggering(true);
    try {
      const result = await dispatch(
        triggerAnalysisJob({ documentId, claimedOnly, matterId, queue: true })
      );
      if (triggerAnalysisJob.fulfilled.match(result)) {
        setQueueOffer(null);
      }
    } finally {
      setIsTriggering(false);
    }
  };

  // Only bail to the "no context" empty state when there's neither a
  // matter nor a document. The matter-wide Brief renders fine at the
  // matter root (documentId == null) — hiding it there was part of the
  // pane reacting to viewer navigation.
  if (!documentId && !matterId) {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyIconRing>
          <Sparkle size={32} weight="duotone" />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>No document selected</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Open a document from the file tree to run an AI analysis.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  // Show a skeleton while the initial jobs-list hydration is in flight.
  // Without this the empty-state CTA flashes into view during the mount
  // fetch — and `isRunning` reads from the still-empty cached jobs
  // array, so a fast click could trigger a duplicate job if the server
  // already has one running. Matches BriefingTab's idle/loading guard.
  if (jobsState.status === "idle" || jobsState.status === "loading") {
    return (
      <AIPaneScrollBody>
        <BriefingSkeletonGroup>
          <BriefingSkeletonBlock $w="40%" $h={20} />
          <BriefingSkeletonBlock $w="100%" $h={14} />
          <BriefingSkeletonBlock $w="86%" $h={14} />
          <BriefingSkeletonBlock $w="55%" $h={36} />
        </BriefingSkeletonGroup>
      </AIPaneScrollBody>
    );
  }

  // Have an active job to render — running, failed, cancelled, or a
  // completed run still cached from earlier. Routing:
  //
  //   completed | completed_with_errors  → AnalysisReport (Phase 4)
  //                                         full markdown report with
  //                                         strategic overview + per-
  //                                         condition collapsibles +
  //                                         Export action.
  //   running                            → AnalysisProgressView
  //                                         (live status pill +
  //                                         progress bar + condition
  //                                         checklist).
  //   failed | cancelled                 → AnalysisProgressView with
  //                                         the Try Again button.
  //
  // We deliberately don't gate on `isRunning` here (Phase 1 did) —
  // that would hide a freshly-completed job behind the empty-state
  // CTA, which is the regression Phase 3 + Phase 4 are designed to fix.
  if (activeJob) {
    const isCompleteish =
      activeJob.status === "completed" ||
      activeJob.status === "completed_with_errors";
    if (isCompleteish) {
      return <AnalysisReport jobId={activeJob.id} fallbackMatterId={matterId} />;
    }
    const showRunningStub =
      activeJob.status === "running" &&
      (activeJob.total_conditions == null || activeJob.total_conditions === 0) &&
      (!Array.isArray(activeJob.conditions) || activeJob.conditions.length === 0);
    if (showRunningStub) {
      // Pre-discovery state: backend hasn't returned `total_conditions`
      // yet (first 5–10 seconds after trigger). The progress view
      // would show "0 / ?" with an empty checklist — calmer to show
      // the existing "Analyzing…" empty-state stub during this gap.
      return (
        <AIPaneScrollBody>
          <AnalysisEmptyWrapper>
            <AnalysisEmptyIconRing>
              <Sparkle size={32} weight="duotone" />
            </AnalysisEmptyIconRing>
            <AnalysisEmptyTitle>Starting analysis…</AnalysisEmptyTitle>
            <AnalysisEmptyDescription>
              The AI is discovering claimed conditions. This usually
              takes the first 10–20 seconds.
            </AnalysisEmptyDescription>
          </AnalysisEmptyWrapper>
        </AIPaneScrollBody>
      );
    }
    return (
      <AnalysisProgressView
        // The job's own document, not the viewer's: matter-first means
        // the rendered job usually wasn't triggered from the document
        // currently open (or any document is open at all). Try Again
        // must re-run against the job's original target.
        documentId={activeJob?.document ?? documentId}
        matterId={matterId}
        job={activeJob}
        isTriggering={isTriggering}
      />
    );
  }

  // No jobs cached for this matter/doc yet — render the Run CTA.
  // Trigger errors land in the PER-DOC slot (the matter cache is
  // never poisoned by a failed POST), so with matter-first rendering
  // the banner must read the raw doc slot too or a failed Run would
  // be silently invisible.
  const fetchOrTriggerError =
    jobsState.status === "failed"
      ? jobsState.error || "Failed to load analysis jobs."
      : docJobsState.status === "failed"
        ? docJobsState.error || "Failed to start analysis."
        : null;

  // The queueable-429 payload snapshot: "your Brief on Smith has
  // finished 3 of 9 conditions" (or "is still discovering conditions"
  // pre-discovery). Rendered INSTEAD of the red error banner.
  const runningBrief = queueOffer?.runningBrief || null;
  const queueOfferMessage = runningBrief
    ? `Your Brief${
        runningBrief.matter_name ? ` on ${runningBrief.matter_name}` : ""
      } ${
        runningBrief.total_conditions
          ? `has finished ${runningBrief.completed_conditions} of ${runningBrief.total_conditions} conditions`
          : "is still discovering conditions"
      }.`
    : null;

  return (
    <AnalysisEmptyWrapper>
      <AnalysisEmptyIconRing>
        <Sparkle size={32} weight="duotone" />
      </AnalysisEmptyIconRing>
      <AnalysisEmptyTitle>Run AI analysis</AnalysisEmptyTitle>
      <AnalysisEmptyDescription>
        Discovers each VA condition and generates a per-condition legal
        analysis with graph-sourced evidence. Typically takes 2–10 minutes.
      </AnalysisEmptyDescription>

      {queueOffer ? (
        <>
          <AnalysisEmptyDescription data-testid="ai-queue-offer">
            {queueOfferMessage} Queue this one to start automatically when
            it finishes.
          </AnalysisEmptyDescription>
          <Button
            type="primary"
            icon={<Sparkle size={14} weight="fill" />}
            onClick={handleQueue}
            loading={isTriggering}
            disabled={!documentId || isTriggering}
          >
            Queue this Brief
          </Button>
        </>
      ) : (
        fetchOrTriggerError && (
          <>
            <AnalysisErrorBanner role="alert">
              {fetchOrTriggerError}
            </AnalysisErrorBanner>
            <Button
              icon={<ArrowsClockwise size={14} weight="bold" />}
              onClick={handleRetry}
            >
              Retry
            </Button>
          </>
        )
      )}

      <Checkbox
        checked={claimedOnly}
        onChange={(e) => setClaimedOnly(e.target.checked)}
      >
        Focus on claimed, decided, or pending conditions
      </Checkbox>

      {/* Starting a NEW analysis still needs a document to POST
          against — that's the one place the viewer context enters this
          pane, read at click time, never reactively. */}
      {!documentId && (
        <AnalysisEmptyDescription>
          Open a document from the file tree to start a new analysis.
        </AnalysisEmptyDescription>
      )}

      <Button
        type="primary"
        size="large"
        icon={<Sparkle size={16} weight="fill" />}
        onClick={handleRun}
        loading={isTriggering}
        disabled={!documentId || isRunning || isTriggering}
      >
        Run AI Analysis
      </Button>
    </AnalysisEmptyWrapper>
  );
};

AnalysisTab.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isActive: PropTypes.bool,
};

AnalysisTab.defaultProps = {
  documentId: null,
  matterId: null,
  isActive: true,
};

export default AnalysisTab;
